import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { catchError, debounceTime, filter, map, mergeMap, of, switchMap } from 'rxjs';
import { ArticleKind, ICertificate } from '../../models/article.model';
import { IBundlePosition, PositionKind } from '../../models/position.model';
import { AddressForm, OccasionType } from '../../models/product.model';
import { CertificateApiService } from '../../services/api/certificate.service';
import { IErrorResult } from '../../services/dto.service';
import { GoogleService } from '../../services/utility/google.service';
import { AppState, ICertificateState } from '../app.state';
import { ArticleSelector } from '../article/article.selectors';
import { CartSelector } from '../cart/cart.selectors';
import { fromCertificateActions } from '../certificate/certificate.actions';
import { CertificateSelector } from '../certificate/certificate.selectors';
import { EntityEffects } from '../entity.effects';
import { NewspaperSelector } from '../newspaper/newspaper.selectors';
import { RouterSelector } from '../router/router.selectors';

@Injectable()
export class CertificateEffects extends EntityEffects<ICertificate, ICertificateState> {
	private customAnniversaries = [18];

	constructor(
		actions$: Actions,
		store: Store<AppState>,
		private certificateApiService: CertificateApiService,
		private certificateSelector: CertificateSelector,
		routerSelector: RouterSelector,
		private cartSelector: CartSelector,
		private articleSelector: ArticleSelector,
		private googleService: GoogleService,
		private newspaperSelector: NewspaperSelector
	) {
		super(actions$, store, certificateApiService, certificateSelector, routerSelector, fromCertificateActions);
	}

	public onLoad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.load),
			debounceTime(10),
			concatLatestFrom(() => this.store.select(this.certificateSelector.paging)),
			switchMap(([, paging]) =>
				this.certificateApiService.search({ articleKind: ArticleKind.Certificate }, paging).pipe(
					map(data => this.entityActions.loaded({ list: data })),
					catchError((response: IErrorResult) => of(this.entityActions.failed({ message: response.error?.error || response.error?.message })))
				)
			)
		)
	);

	public onLoaded$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.loaded),
			filter(({ list }) => list.length > 0),
			concatLatestFrom(() => this.store.select(this.certificateSelector.selected)),
			concatLatestFrom(() => this.store.select(this.certificateSelector.customMessage)),
			concatLatestFrom(() => this.store.select(this.cartSelector.selectedPostion)),
			mergeMap(([[[{ list }, selected], customMessage], position]) => {
				let entity = list[0];
				const message = customMessage || entity.message;

				if (position != null && position.positionKind == PositionKind.Bundle) {
					const bundlePosition = position as IBundlePosition;
					const article = bundlePosition.articles.find(x => x.articleKind == ArticleKind.Certificate);

					if (article != null) {
						entity = { ...entity, ...article, message, isCustomized: true, processInstance: entity.processInstance, _id: entity._id };
					}
				} else if (selected != null) {
					entity = { ...entity, ...selected, message, isCustomized: true, processInstance: entity.processInstance, _id: entity._id };
				}

				return [this.entityActions.selected({ entity }), fromCertificateActions.customMessageChanged({ customMessage: entity.message }), fromCertificateActions.originalMessageChanged({ originalMessage: entity.message })];
			})
		)
	);

	public onSelected$ = createEffect(() =>
		this.actions$.pipe(
			ofType(this.entityActions.selected),
			concatLatestFrom(() => this.store.select(this.newspaperSelector.date)),
			concatLatestFrom(() => this.store.select(this.certificateSelector.selected)),
			filter(([[, date], certificate]) => date != null && certificate != null && !certificate.date),
			map(([[, date]]) => fromCertificateActions.dateChanged({ date }))
		)
	);

	public onDatechanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCertificateActions.dateChanged),
			map(({ date }) => this.findYears(date)),
			map(years => years.find(year => this.customAnniversaries.indexOf(year) > -1 || year % 5 == 0)),
			map(anniversary => fromCertificateActions.anniversaryChanged({ anniversary }))
		)
	);

	public isCustomizedChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCertificateActions.isCustomizedChanged),
			concatLatestFrom(() => this.store.select(this.certificateSelector.customMessage)),
			concatLatestFrom(() => this.store.select(this.certificateSelector.originalMessage)),
			map(([[{ isCustomized }, customMessage], originalMessage]) => fromCertificateActions.messageChanged({ message: isCustomized ? customMessage : originalMessage }))
		)
	);

	public occasionChangedForPartner$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCertificateActions.occasionChanged),
			map(() => fromCertificateActions.partnerChanged({ partner: null }))
		)
	);

	public occasionChangedForText$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCertificateActions.occasionChanged, fromCertificateActions.addressFormChanged),
			concatLatestFrom(() => this.store.select(this.articleSelector.mediaArticle)),
			concatLatestFrom(() => this.store.select(this.certificateSelector.selected)),
			filter(([[, mediaArticle], certificate]) => mediaArticle != null && certificate != null),
			map(([[, mediaArticle], certificate]) => {
				if (mediaArticle.product.publicationCycle.name.includes('täglich')) {
					if (certificate.occasion == OccasionType.Birthday || certificate.occasion == OccasionType.Christmas) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Dein Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />vom Tag Deiner Geburt.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />vom Tag Ihrer Geburt.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Silver || certificate.occasion == OccasionType.Gold || certificate.occasion == OccasionType.Diamond) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Euer Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lest Ihr in der Zeitung<br />vom Tag Eurer Hochzeit.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />vom Tag Ihrer Hochzeit.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Retirement) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Deines Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />von diesem Tag.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Ihres Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />von diesem Tag.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Custom) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format('DD. MMMM YYYY')}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />von diesem Tag.<br /><br />`,
							});
						} else {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format('DD. MMMM YYYY')}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />von diesem Tag.<br /><br />`,
							});
						}
					}
				}

				if (mediaArticle.product.publicationCycle.name.includes('wöchentlich')) {
					if (certificate.occasion == OccasionType.Birthday || certificate.occasion == OccasionType.Christmas) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Dein Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus der Woche Deiner Geburt.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus der Woche Ihrer Geburt.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Silver || certificate.occasion == OccasionType.Gold || certificate.occasion == OccasionType.Diamond) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Euer Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lest Ihr in der Zeitung<br />aus der Woche Eurer Hochzeit.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus der Woche Ihrer Hochzeit.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Retirement) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Deines Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus der Woche Deines Dienstantritts.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Ihres Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus der Woche Ihres Dienstantritts.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Custom) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format(
									'DD. MMMM YYYY'
								)}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus dieser Woche.<br /><br />`,
							});
						} else {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format(
									'DD. MMMM YYYY'
								)}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus dieser Woche.<br /><br />`,
							});
						}
					}
				}

				if (mediaArticle.product.publicationCycle.name.includes('monatlich')) {
					if (certificate.occasion == OccasionType.Birthday) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Dein Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus dem Monat Deiner Geburt.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Geburtstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus dem Monat Ihrer Geburt.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Silver || certificate.occasion == OccasionType.Gold || certificate.occasion == OccasionType.Diamond) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Euer Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lest Ihr in der Zeitung<br />aus dem Monat Eurer Hochzeit.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Ihr Hochzeitstag war<br />ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus dem Monat Ihrer Hochzeit.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Retirement) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Deines Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus dem Monat Deines Dienstantritts.<br /><br />',
							});
						} else {
							return fromCertificateActions.textChanged({
								text: 'Der Tag Ihres Dienstantritts<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus dem Monat Ihres Dienstantritts.<br /><br />',
							});
						}
					}

					if (certificate.occasion == OccasionType.Custom) {
						if (certificate.addressForm == AddressForm.Personal) {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format(
									'DD. MMMM YYYY'
								)}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />liest Du in der Zeitung<br />aus diesem Monat.<br /><br />`,
							});
						} else {
							return fromCertificateActions.textChanged({
								text: `Der ${moment(certificate.date).format(
									'DD. MMMM YYYY'
								)}<br />war ein besonderer Tag.<br /><br />Was sonst noch<br />in der Welt geschah,<br />lesen Sie in der Zeitung<br />aus diesem Monat.<br /><br />`,
							});
						}
					}
				}

				return fromCertificateActions.textChanged({
					text: '',
				});
			})
		)
	);

	public occasionChangedForMessage$ = createEffect(() =>
		this.actions$.pipe(
			ofType(fromCertificateActions.occasionChanged),
			concatLatestFrom(() => this.store.select(this.certificateSelector.selected)),
			filter(([, certificate]) => certificate != null),
			map(([, certificate]) => {
				if (certificate.occasion == OccasionType.Christmas) {
					return fromCertificateActions.messageChanged({
						message: 'Frohe Weihnachten',
					});
				}

				if (certificate.occasion == OccasionType.Retirement) {
					return fromCertificateActions.messageChanged({
						message: 'Alles Gute',
					});
				}

				return fromCertificateActions.messageChanged({
					message: 'Herzlichen Glückwunsch',
				});
			})
		)
	);

	public onOccasionChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.occasionChanged),
				mergeMap(({ occasion }) => [this.googleService.changeOccasion(occasion)])
			),
		{ dispatch: false }
	);

	public onGifteeChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.gifteeChanged),
				debounceTime(1000),
				mergeMap(({ giftee }) => [this.googleService.changeGiftee(giftee)])
			),
		{ dispatch: false }
	);

	public onGifteeBirthNameChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.gifteeBirthNameChanged),
				debounceTime(1000),
				mergeMap(({ gifteeBirthName }) => [this.googleService.changeGifteeBirthName(gifteeBirthName)])
			),
		{ dispatch: false }
	);

	public onPartnerChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.partnerChanged),
				debounceTime(1000),
				mergeMap(({ partner }) => [this.googleService.changePartner(partner)])
			),
		{ dispatch: false }
	);

	public onPartnerBirthNameChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.partnerBirthNameChanged),
				debounceTime(1000),
				mergeMap(({ partnerBirthName }) => [this.googleService.changePartnerBirthName(partnerBirthName)])
			),
		{ dispatch: false }
	);

	public onAddressFormChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.addressFormChanged),
				mergeMap(({ addressForm }) => [this.googleService.changeAddressForm(addressForm)])
			),
		{ dispatch: false }
	);

	public onCustomMessageChangedForAnalysis$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(fromCertificateActions.customMessageChanged),
				concatLatestFrom(() => this.store.select(this.certificateSelector.originalMessage)),
				filter(([{ customMessage }, originalMessage]) => customMessage != originalMessage),
				debounceTime(1000),
				mergeMap(([{ customMessage }, originalMessage]) => [this.googleService.changeCustomMessage(customMessage, originalMessage)])
			),
		{ dispatch: false }
	);

	/*
	 * See documentation:
	 * https://identicgroup.atlassian.net/wiki/spaces/PI/pages/25264129/Geschenkurkunde+bis+2022
	 */
	private findYears(date: Date): number[] {
		const result: number[] = [];

		const today = moment().startOf('day');
		const currentYear = today.year();
		const anniversaryDate = moment(date).startOf('day').year(currentYear);
		const anniversaryYear = moment(date).startOf('day').year();
		const nextYearLimit = moment().month(10).date(1).subtract(1, 'day');
		const previousYearLimit = moment().month(2).date(1);

		// Standard
		// Jubiläumsdatum liegt NACH dem aktuellen Datum
		if (anniversaryDate.isSameOrAfter(today)) {
			result.push(currentYear - anniversaryYear);
		}

		// Sonderfall 1
		// Aktuelles Datum ist NACH dem 01. November && Jubiläumsdatum liegt VOR dem aktuellen Datum
		if (today.isAfter(nextYearLimit) && anniversaryDate.isBefore(today)) {
			result.push(currentYear - anniversaryYear + 1);
		}

		// Sonderfall 2
		// Aktuelles Datum liegt VOR dem 01. März && Jubiläumsdatum liegt ZWISCHEN dem 01. November und dem aktuellen Datum
		if (today.isBefore(previousYearLimit) && anniversaryDate.isBetween(nextYearLimit, today)) {
			result.push(currentYear - anniversaryYear - 1);
		}

		return result;
	}
}
